<template>
  <v-card class="invoice-document-controls" color="surface">
    <v-card-title class="d-flex align-center text-subtitle-1 py-3">
      {{ documentLabel }} details
      <v-spacer/>
      <v-btn
        prepend-icon="mdi-palette-outline"
        size="small"
        variant="tonal"
        @click="designDrawer = true"
      >
        Customize design
      </v-btn>
    </v-card-title>

    <InvoiceDesignDrawer v-model="designDrawer" @saved="onDesignSaved"/>
    <v-card-text>
      <v-row density="comfortable">
        <v-col cols="12" md="2" sm="4">
          <v-text-field
            v-model="invoiceNumber"
            :disabled="isFinalized"
            :label="`${documentLabel} #`"
            :loading="orderLoading.update"
            density="compact"
            hide-details
            type="number"
            variant="outlined"
            @blur="saveNumber"
            @keyup.enter="saveNumber"
          />
        </v-col>
        <v-col cols="12" md="3" sm="4">
          <v-text-field
            v-model="invoiceDate"
            :disabled="isFinalized"
            :loading="orderLoading.update"
            density="compact"
            hide-details
            label="Date"
            type="date"
            variant="outlined"
            @change="saveDate"
          />
        </v-col>
        <v-col cols="12" md="4" sm="4">
          <v-switch
            v-model="termsAppend"
            :disabled="isFinalized"
            :loading="orderLoading.update"
            color="primary"
            hide-details
            inset
            label="Include terms & conditions"
            v-bind="{ 'true-icon': 'mdi-check', 'false-icon':  'mdi-close'}"
            @update:model-value="saveTermsAppend"
          />
        </v-col>
      </v-row>

      <v-textarea
        v-model="billToAddress"
        :disabled="isFinalized"
        :loading="orderLoading.update"
        class="mt-2"
        density="compact"
        hide-details
        label="Bill to address"
        rows="3"
        variant="outlined"
        @blur="saveBillToAddress"
      />
      <div v-if="customerAddress && billToAddress !== customerAddress && !isFinalized" class="d-flex justify-end mt-1">
        <v-btn size="x-small" variant="text" @click="useCustomerAddress">Use customer's address</v-btn>
      </div>

      <v-textarea
        v-model="reference"
        :disabled="isFinalized"
        :loading="orderLoading.update"
        class="mt-2"
        density="compact"
        hide-details
        label="Reference"
        rows="2"
        variant="outlined"
        @blur="saveReference"
      />

      <div class="mt-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-body-2 font-weight-medium">Footer</span>
          <v-btn
            v-if="!editFooter && !isFinalized"
            size="small"
            variant="text"
            @click="openFooterEditor"
          >
            {{ footerPreview ? 'Edit footer' : 'Add footer' }}
          </v-btn>
        </div>

        <div
          v-if="!editFooter && footerPreview"
          class="invoice-footer-preview text-body-2"
          v-html="footerPreview"
        />

        <div v-if="editFooter">
          <div id="invoice-footer-editor" class="invoice-footer-editor"/>
          <div class="d-flex justify-end ga-2 mt-2">
            <v-btn size="small" variant="text" @click="cancelFooterEdit">{{ $t('cancel') }}</v-btn>
            <v-btn :loading="orderLoading.update" color="primary" size="small" variant="tonal" @click="saveFooter">Save
              footer
            </v-btn>
          </div>
        </div>
      </div>

      <div v-if="vehicleLines.length" class="mt-4">
        <span class="text-body-2 font-weight-medium">Vehicle details</span>
        <p class="text-caption text-medium-emphasis mb-2">
          Override what shows on this invoice — e.g. a different drop-off location. Only this invoice changes.
        </p>

        <div v-for="line in vehicleLines" :key="line.uuid" class="mb-2">
          <div class="d-flex align-center justify-space-between">
            <span class="text-body-2">{{ line.title }}</span>
            <v-btn
              v-if="editingVehicleLine !== line.uuid"
              size="small"
              variant="text"
              @click="editingVehicleLine = line.uuid"
            >
              Edit
            </v-btn>
          </div>

          <VehicleDetailsEditor
            v-if="editingVehicleLine === line.uuid"
            :details="line.metadata?.vehicle_details"
            :document-uuid="documentUuid"
            :line-uuid="line.uuid"
            class="mt-1"
            @close="editingVehicleLine = null"
            @saved="onVehicleDetailsSaved"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import {computed, onBeforeUnmount, ref, watch} from 'vue';
import EditorJS, {type OutputData} from '@editorjs/editorjs';
import Header from '@editorjs/header';
import dayjs from 'dayjs';
import {useRoute} from 'vue-router';
import {useOrders} from '@/composables/useOrders';
import VehicleDetailsEditor from '@/components/dashboard/documents/VehicleDetailsEditor.vue';
import InvoiceDesignDrawer from '@/components/dashboard/documents/InvoiceDesignDrawer.vue';
import type {OrderLine} from '@/types/order';

const route = useRoute();
const {orderDocument, orderLoading, updateOrderDocument, getOrderDocument} = useOrders();

const documentUuid = computed(() => route.params.uuid as string);
const isFinalized = computed(() => Boolean(orderDocument.value?.finalized));
const documentLabel = computed(() => (orderDocument.value?.type === 'Document::Quote' ? 'Quote' : 'Invoice'));

const vehicleLines = computed(
  () => (orderDocument.value?.lines ?? []).filter(
    (line): line is OrderLine & { uuid: string } => line.item_type === 'vehicle' && !!line.uuid,
  ),
);
const editingVehicleLine = ref<string | null>(null);
const designDrawer = ref(false);

const emit = defineEmits<{ documentUpdated: [] }>();

const onDesignSaved = (): void => {
  emit('documentUpdated');
};

const onVehicleDetailsSaved = async (): Promise<void> => {
  editingVehicleLine.value = null;
  if (documentUuid.value) {
    await getOrderDocument(documentUuid.value);
  }
  // Re-render the server-rendered invoice preview with the new details.
  emit('documentUpdated');
};

const invoiceNumber = ref('');
const invoiceDate = ref('');
const reference = ref('');
const billToAddress = ref('');
const customerAddress = computed<string>(() => orderDocument.value?.customer?.formatted_address ?? '');
const termsAppend = ref(false);
const editFooter = ref(false);
const footerBlocks = ref<OutputData | null>(null);
const editor = ref<EditorJS | null>(null);

const footerPreview = computed(() => convertBlocksToHtml(footerBlocks.value));

watch(orderDocument, (document) => {
  if (!document) {
    return;
  }

  invoiceNumber.value = document.number != null ? String(document.number) : '';
  invoiceDate.value = document.date ? dayjs(document.date).format('YYYY-MM-DD') : '';
  reference.value = document.reference ?? '';
  billToAddress.value = document.address ?? '';
  termsAppend.value = Boolean(document.terms_append);
  footerBlocks.value = normalizeFooter(document.footer);
}, {immediate: true});

function normalizeFooter(footer: unknown): OutputData | null {
  if (!footer) {
    return null;
  }

  if (typeof footer === 'object' && footer !== null && 'blocks' in footer) {
    return footer as OutputData;
  }

  if (typeof footer === 'string' && footer.trim() !== '') {
    return {
      blocks: [{type: 'paragraph', data: {text: footer}}],
    };
  }

  return null;
}

function convertBlocksToHtml(data: OutputData | null): string {
  if (!data?.blocks?.length) {
    return '';
  }

  return data.blocks.map((block) => {
    const value = block.data?.text ?? '';

    if (block.type === 'header') {
      const level = block.data?.level ?? 2;
      return `<h${level}>${value}</h${level}>`;
    }

    return `<p>${value}</p>`;
  }).join('');
}

async function saveNumber(): Promise<void> {
  const nextNumber = Number(invoiceNumber.value);

  if (!Number.isFinite(nextNumber) || nextNumber === orderDocument.value?.number) {
    return;
  }

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {number: nextNumber},
  });
}

async function saveDate(): Promise<void> {
  const formatted = invoiceDate.value ? dayjs(invoiceDate.value).format('YYYY-MM-DD') : null;
  const current = orderDocument.value?.date ? dayjs(orderDocument.value.date).format('YYYY-MM-DD') : null;

  if (formatted === current) {
    return;
  }

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {date: formatted},
  });
}

async function saveBillToAddress(): Promise<void> {
  if (billToAddress.value === (orderDocument.value?.address ?? '')) {
    return;
  }

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {address: billToAddress.value || null},
  });
}

function useCustomerAddress(): void {
  billToAddress.value = customerAddress.value;
  void saveBillToAddress();
}

async function saveReference(): Promise<void> {
  if (reference.value === (orderDocument.value?.reference ?? '')) {
    return;
  }

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {reference: reference.value || null},
  });
}

async function saveTermsAppend(value: boolean | null): Promise<void> {
  if (value === null || value === Boolean(orderDocument.value?.terms_append)) {
    return;
  }

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {terms_append: value},
  });
}

function openFooterEditor(): void {
  destroyEditor();
  editFooter.value = true;

  requestAnimationFrame(() => {
    editor.value = new EditorJS({
      holder: 'invoice-footer-editor',
      autofocus: true,
      tools: {header: Header},
      data: footerBlocks.value ?? undefined,
    });
  });
}

function cancelFooterEdit(): void {
  editFooter.value = false;
  destroyEditor();
  footerBlocks.value = normalizeFooter(orderDocument.value?.footer);
}

async function saveFooter(): Promise<void> {
  if (!editor.value) {
    return;
  }

  const outputData = await editor.value.save();

  await updateOrderDocument({
    documentUuid: documentUuid.value,
    attributes: {footer: outputData},
  });

  footerBlocks.value = outputData;
  editFooter.value = false;
  destroyEditor();
}

function destroyEditor(): void {
  editor.value?.destroy();
  editor.value = null;
}

onBeforeUnmount(() => {
  destroyEditor();
});
</script>

<style scoped>
.invoice-document-controls {
  width: 100%;
}

.invoice-footer-preview,
.invoice-footer-editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 12px;
}

.invoice-footer-preview {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.invoice-footer-editor {
  min-height: 120px;
  background: rgb(var(--v-theme-surface));
}
</style>
